// Phase 37 Batch AU — Post-signup viral referral loop UI.
//
// Renders inside `WaitlistModal` once the POST /api/waitlist succeeds.
// Replaces the simple "Got it" confirmation with:
//
//   1. Confirmation message + email echo
//   2. Personal referral link with one-click copy
//   3. Native share buttons (X, LinkedIn, WhatsApp, Email)
//   4. Live progress bar — polls /api/waitlist/referral-stats every
//      15s AND on window focus, ticks the count up as friends sign up
//   5. Reward state copy that flips from "Refer 3" → "Unlocked!"
//
// Batch BC — share-link construction and channel intents now live in
// `lib/referralShare.js`. The dashboard "Refer Friends" widget consumes
// the same module so a future channel addition or share-link template
// change lands in ONE place instead of two.
//
// All network errors are silent (the share UX must keep working even
// if stats fetch is flaky); we just show the last known count.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Copy as CopyIcon, Sparkles } from "lucide-react";
import {
  trackReferralLinkCopied,
  trackReferralShareClicked,
  trackReferralGoalUnlocked,
} from "@/lib/landingEvents";
import {
  REWARD_TARGET,
  SHARE_CHANNELS,
  SHARE_MESSAGE_WAITLIST,
  buildShareLink,
  shareIntentUrl,
  channelLabel,
} from "@/lib/referralShare";

const POLL_INTERVAL_MS = 15_000;

// Dev-only debug log. `console.debug` is already at browser "verbose"
// level so end-users never see it in normal devtools; we don't need
// our own NODE_ENV gate — CRA's tree-shaker can't strip it but the
// log is cheap (< 1 KB minified, fires once every 15s) and makes
// future polling regressions trivial to diagnose in Chrome DevTools
// (set the Console log level to "Verbose" or filter by "[referral-poll]").
function _debug(...args) {
  if (typeof console !== "undefined" && console.debug) {
    try { console.debug(...args); } catch { /* ignore */ }
  }
}

export default function WaitlistSuccess({ email, referralCode, onClose }) {
  const [stats, setStats] = useState({
    referred_count: 0,
    unlocked: false,
    target: REWARD_TARGET,
  });
  const [copied, setCopied] = useState(false);
  const unlockedFiredRef = useRef(false);
  const copyTimerRef = useRef(null);

  const shareLink = useMemo(() => buildShareLink(referralCode), [referralCode]);

  const fetchStats = useCallback(async () => {
    if (!referralCode) return;
    try {
      const res = await api.get(
        `/waitlist/referral-stats?ref=${encodeURIComponent(referralCode)}`,
      );
      const next = res.data || {};
      _debug("[referral-poll]", {
        code: referralCode,
        referred_count: next.referred_count,
        unlocked: next.unlocked,
        target: next.target,
      });
      setStats((prev) => {
        const merged = {
          referred_count: Number(next.referred_count ?? prev.referred_count ?? 0),
          unlocked: Boolean(next.unlocked),
          target: Number(next.target ?? prev.target ?? REWARD_TARGET),
        };
        // Fire the unlock event once, the FIRST time the count flips
        // past the target. Guarded by a ref so subsequent polls don't
        // re-emit even if the user's count keeps climbing.
        if (
          merged.unlocked &&
          !unlockedFiredRef.current &&
          merged.referred_count >= merged.target
        ) {
          unlockedFiredRef.current = true;
          trackReferralGoalUnlocked({
            referral_code: referralCode,
            referred_count: merged.referred_count,
          });
        }
        return merged;
      });
    } catch (e) {
      _debug("[referral-poll] error", e);
      /* keep last-known stats — never break the share UX */
    }
  }, [referralCode]);

  // Poll every 15s + refresh on window focus AND on tab
  // visibilitychange (covers Safari/iOS where window.focus is flaky).
  // Both listeners + the interval clean up on unmount.
  useEffect(() => {
    if (!referralCode) return undefined;
    fetchStats();
    const id = window.setInterval(fetchStats, POLL_INTERVAL_MS);
    const onFocus = () => {
      _debug("[referral-poll] focus → refetch");
      fetchStats();
    };
    const onVisibility = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        _debug("[referral-poll] visibilitychange:visible → refetch");
        fetchStats();
      }
    };
    window.addEventListener("focus", onFocus);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
  }, [referralCode, fetchStats]);

  // Belt-and-braces cleanup for the "Copied!" timer.
  useEffect(() => () => {
    if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      trackReferralLinkCopied({ referral_code: referralCode });
      if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fall back to legacy execCommand for older browsers — silent.
      try {
        const ta = document.createElement("textarea");
        ta.value = shareLink;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopied(true);
        trackReferralLinkCopied({ referral_code: referralCode });
        copyTimerRef.current = window.setTimeout(() => setCopied(false), 2000);
      } catch { /* clipboard blocked — give up silently */ }
    }
  };

  const handleShare = (channel) => {
    trackReferralShareClicked({ channel, referral_code: referralCode });
    if (typeof window !== "undefined") {
      window.open(
        shareIntentUrl(channel, shareLink, { message: SHARE_MESSAGE_WAITLIST }),
        "_blank",
        "noopener,noreferrer",
      );
    }
  };

  const count = Math.max(0, stats.referred_count || 0);
  const target = Math.max(1, stats.target || REWARD_TARGET);
  const progressPct = Math.min(100, Math.round((count / target) * 100));
  const remaining = Math.max(0, target - count);
  const isUnlocked = count >= target;

  let rewardCopy;
  if (isUnlocked) {
    rewardCopy =
      "Unlocked! You'll get Founders Pricing 24 hours before public launch.";
  } else if (count === 0) {
    rewardCopy = `Refer ${target} friends to unlock Founders Pricing 24 hours early.`;
  } else {
    rewardCopy = `${remaining} more to unlock Founders Pricing 24h early.`;
  }

  return (
    <div className="space-y-5 py-1" data-testid="waitlist-success-screen">
      <div className="space-y-2 text-center">
        <div
          className={
            "mx-auto flex h-12 w-12 items-center justify-center rounded-full " +
            (isUnlocked
              ? "bg-emerald-100 text-emerald-600"
              : "bg-indigo-100 text-indigo-600")
          }
        >
          {isUnlocked ? (
            <Sparkles className="h-6 w-6" data-testid="success-icon-unlocked" />
          ) : (
            <CheckCircle2 className="h-6 w-6" data-testid="success-icon" />
          )}
        </div>
        <h3 className="text-lg font-semibold text-slate-900">
          You&rsquo;re on the list
        </h3>
        {email && (
          <p
            className="text-sm text-slate-600"
            data-testid="waitlist-success-email"
          >
            Confirmation sent to{" "}
            <span className="font-medium text-slate-800">{email}</span>
          </p>
        )}
      </div>

      {/* Referral link + copy */}
      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Your personal invite link
        </p>
        <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-1.5">
          <input
            readOnly
            value={shareLink}
            className="flex-1 truncate bg-transparent px-2 py-1.5 text-xs text-slate-700 outline-none sm:text-sm"
            data-testid="referral-link-input"
            aria-label="Your referral link"
            onFocus={(e) => e.target.select()}
          />
          <Button
            type="button"
            size="sm"
            onClick={handleCopy}
            className={
              copied
                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                : "bg-indigo-600 text-white hover:bg-indigo-700"
            }
            data-testid="referral-copy-button"
            aria-label={copied ? "Link copied" : "Copy referral link"}
          >
            <CopyIcon className="mr-1 h-3.5 w-3.5" />
            {copied ? "Copied!" : "Copy"}
          </Button>
        </div>
      </div>

      {/* Share intents */}
      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Share with friends
        </p>
        <div className="grid grid-cols-4 gap-2" data-testid="referral-share-row">
          {SHARE_CHANNELS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => handleShare(id)}
              aria-label={label}
              data-testid={`referral-share-${id}`}
              className="flex flex-col items-center gap-1 rounded-xl border border-slate-200 bg-white px-2 py-3 text-xs font-medium text-slate-700 transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              <span>{channelLabel(id)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Progress + reward */}
      <div
        className={
          "space-y-2 rounded-2xl border p-4 " +
          (isUnlocked
            ? "border-emerald-200 bg-emerald-50/60"
            : "border-indigo-100 bg-indigo-50/60")
        }
        data-testid="referral-progress-card"
      >
        <div className="flex items-center justify-between text-sm">
          <span
            className={
              "font-semibold " +
              (isUnlocked ? "text-emerald-700" : "text-indigo-700")
            }
            data-testid="referral-progress-count"
          >
            {count} of {target} friends joined
          </span>
          {isUnlocked && (
            <span
              className="flex items-center gap-1 text-xs font-semibold text-emerald-700"
              data-testid="referral-unlocked-badge"
            >
              <Sparkles className="h-3 w-3" />
              Unlocked
            </span>
          )}
        </div>
        <div
          className="h-2 w-full overflow-hidden rounded-full bg-white/70"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={target}
          aria-valuenow={count}
        >
          <div
            className={
              "h-full rounded-full transition-all duration-500 " +
              (isUnlocked ? "bg-emerald-500" : "bg-indigo-600")
            }
            style={{ width: `${progressPct}%` }}
            data-testid="referral-progress-bar"
          />
        </div>
        <p
          className={
            "text-xs " + (isUnlocked ? "text-emerald-800" : "text-slate-600")
          }
          data-testid="referral-reward-copy"
        >
          {rewardCopy}
        </p>
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={onClose}
        className="w-full"
        data-testid="waitlist-success-close"
      >
        Close
      </Button>
    </div>
  );
}
